import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {Accordion, AccordionTab} from 'primereact/accordion';
import {apiRequest, downloadFileOutcome, formatBigNo, toast} from "@/Helpers/general";
import {Button} from "primereact/button";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";
import {CLAIM_RULESETS_ROWS_NO} from "@/Config/constants";
import {AppContext} from "@/Components/Context/AppContext";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import InfoMessageTemplate from "@/Components/Templates/InfoMessageTemplate";
import OutcomeAmountsModal from "@/Components/Modals/OutcomeAmountsModal";
import {CaseContext} from "@/Components/Context/CaseContext";
import {Tooltip} from "primereact/tooltip";

const OutcomesWrap = styled.div`
    .outcomes-wrap {
        min-height: 35vh;
        position: relative;
        padding: 0 0.5rem;
    }
`;

/**
 * Component related to the claim's outcomes
 */
const Outcomes = ({caseId, disabled = false}) => {
    const {toastRef} = useContext(AppContext);
    const {
        outcomeDays,
        outcomeAmount,
        outcomeTotal,
        setOutcomeTotal,
        setOutcomeDays,
        setOutcomeAmount,
        requestedOutcomeDays,
        setRequestedOutcomeDays,
        requestedOutcomeAmount,
        setRequestedOutcomeAmount,
        requestedTotal,
        setRequestedTotal,
        gtaResponseOutcomeAmount,
        setGtaResponseOutcomeAmount,
        gtaResponseOutcomeDays,
        setGtaResponseOutcomeDays,
        gtaResponseOutcomeTotal,
        setGtaResponseOutcomeTotal,
        getCaseData,
        caseData,
        refreshBtnLoading
    } = useContext(CaseContext);

    const [outcomes, setOutcomes] = useState([]);
    const [outcomesLoading, setOutcomesLoading] = useState(false);
    const [activeAccordionIndex, setActiveAccordionIndex] = useState([0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [statuses, setStatuses] = useState();
    const [recommendedStatus, setRecommendedStatus] = useState();
    const [statusLoading, setStatusLoading] = useState(false);
    const [completeCaseLoading, setCompleteCaseLoading] = useState(false);
    const [showEditAmountsModal, setShowEditAmountsModal] = useState(false);
    const [disabledDownload, setDisabledDownload] = useState(false);

    /**
     * Accordion header
     *
     * @param {object} outcome data related to the outcome
     */
    const headerTemplate = (outcome) => {
        return (
            <div className="flex w-full justify-content-between">
                <span>{outcome?.rule_action?.name ?? ''}</span>
            </div>
        );
    };

    const downloadReport = () => {
        downloadFileOutcome({
            url: `case/${caseId}/outcomes/rule-audit`,
            onSuccess: () => {
                toast(toastRef, 'success', 'The requested data successfully downloaded.');
            },
            onFailure: (_, status) => {
                if (status === 404) {
                    toast(toastRef, 'error', 'No logs available. Please check if any logs have been generated.');
                    setDisabledDownload(true);
                } else {
                    toast(toastRef, 'error', 'An error occurred during the archiving process, please try again later!');
                }
            },
        });
    };

    /**
     * Change status
     */
    const changeStatus = async () => {
        if (recommendedStatus?.id) {
            setStatusLoading(true);

            apiRequest('post', 'case/' + caseId + '/status', {
                data: {status: recommendedStatus.id},
                onSuccess: (response) => {
                    setStatusLoading(false);

                    toast(toastRef, 'success', 'The case status has been updated.');
                },
                onFailure: (error) => {
                    setStatusLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            toast(toastRef, 'error', 'Error updating status.');

                        } else {
                            toast(toastRef, 'error', 'Error updating status.');
                        }

                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error updating status.');
                    }
                }
            });
        }
    };

    /**
     * Complete case
     */
    const completeCase = async () => {
        setCompleteCaseLoading(true);

        apiRequest('post', 'case/' + caseId + '/complete', {
            onSuccess: (response) => {
                setCompleteCaseLoading(false);
                getCaseData();
                loadOutcomes();

                toast(toastRef, 'success', 'The case has been marked as completed.');
            },
            onFailure: (error) => {
                setCompleteCaseLoading(false);

                toast(toastRef, 'error', 'Error completing case.');
            }
        });
    };

    /**
     * Accordion content
     *
     * @param {object} outcome data related to the outcome
     */
    const generateAccordionContent = (outcome) => {
        return (
            <div>
                {outcome.description && <InfoMessageTemplate innerHtml={outcome.description}/>}

                {/* <hr />
                <div className="flex justify-content-between">
                    <div>
                        <span className="font-semibold">Output: </span> {outcome.output}
                    </div>

                    <div>
                        <span className="font-semibold">Date: </span> {formatDate(outcome.created_at)}
                    </div>
                </div> */}
            </div>
        );
    };

    /**
     * Get outcomes from the API
     */
    const loadOutcomes = async () => {
        setOutcomesLoading(true);

        let requestParams = {
            page: currentPage,
            limit: CLAIM_RULESETS_ROWS_NO
        };

        apiRequest('get', 'case/' + caseId + '/outcomes', {
            data: requestParams,
            onSuccess: (response) => {
                setOutcomesLoading(false);

                if (response?.result) {
                    if (response.result?.suggestions) {
                        if (response.result?.suggestions?.statuses?.length > 0) {
                            setStatuses(response.result.suggestions.statuses);
                        }

                        if (response.result?.suggestions?.recommended_status) {
                            let getStatus = response.result.suggestions?.statuses.find((status) => status.id == response.result.suggestions.recommended_status);

                            if (getStatus) {
                                setRecommendedStatus(getStatus);
                            }
                        }
                    }

                    if (response.result?.outcomes?.length > 0) {
                        setOutcomes(response.result.outcomes);
                    }

                    if (response.result?.offers_payments) {
                        setOutcomeDays(response.result?.offers_payments?.response?.total_hire_period);
                        setOutcomeAmount(response.result?.offers_payments?.response?.hire_daily_rate);
                        setOutcomeTotal(response.result?.offers_payments?.response?.total);
                        setRequestedOutcomeDays(response.result?.offers_payments?.request?.total_hire_period);
                        setRequestedOutcomeAmount(response.result?.offers_payments?.request?.hire_daily_rate);
                        setRequestedTotal(response.result?.offers_payments?.request?.total);
                        setGtaResponseOutcomeAmount(response.result?.offers_payments?.gta_response?.hire_daily_rate);
                        setGtaResponseOutcomeDays(response.result?.offers_payments?.gta_response?.total_hire_period);
                        setGtaResponseOutcomeTotal(response.result?.offers_payments?.gta_response?.total);
                    }
                }
            },
            onFailure: () => {
                setOutcomesLoading(false);
            }
        });
    };

    // init
    useEffect(() => {
        if (!caseData.has_pending_outcomes) {
            loadOutcomes();
        }
    }, [caseData.has_pending_outcomes]);

    const outcomeMsg = (
        <>
            <div className="mb-3">After running the listed rulesets, we consider that the most appropriate outcome would
                be:
            </div>
            <div>
                <DropdownInputTemplate name="statuses" placeholder="Select a status" value={recommendedStatus || ''}
                                       options={statuses}
                                       onChangeFunction={(name, value) => setRecommendedStatus(value)} noMargins inline
                                       disabled={disabled} showClear={false}/>
                {!disabled &&
                    <Button label="Change status" className="ml-3" onClick={changeStatus} loading={statusLoading}
                            loadingIcon="pi pi-spin pi-sun" iconPos="center"/>}
                {!disabled && caseData && !caseData.is_complete &&
                    <Button label="Complete case" className="ml-3" onClick={completeCase} loading={completeCaseLoading}
                            loadingIcon="pi pi-spin pi-sun" iconPos="center"/>}
            </div>
        </>
    );

    return (
        <OutcomesWrap>
            <div className="custom-content">
                {
                    caseData.has_pending_outcomes ? <div className="text-center my-5">
                            <p>Outcome Advice tab is currently locked because we are waiting for BHR response. Use the
                                refresh button to check if the process is ready.</p>
                            <Button className="my-3" label="Refresh" icon="pi pi-refresh" onClick={getCaseData}
                                    loading={refreshBtnLoading}/>
                        </div> :

                        <div className="outcomes-wrap">
                            {recommendedStatus ? <>
                                    <div className="mx-3 mb-5">
                                        <InfoMessageTemplate message={outcomeMsg}/>
                                    </div>

                                    <table className="gray-table">
                                        <thead>
                                        <tr>
                                            <td>ITEM</td>
                                            <td>
                                                <div className={'flex gap-2 align-items-center'}>
                                                    CHO REQUESTED PAYMENT
                                                    <i id={'ng-request-header'}
                                                       className={'pi pi-info-circle'}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <div className={'flex gap-2 align-items-center'}>
                                                    NIGHTINGALE RESPONSE
                                                    <i id={'ng-response-header'}
                                                       className={'pi pi-info-circle'}
                                                    />
                                                </div>
                                            </td>
                                            <td>SAVINGS</td>
                                            <td>GTA RESPONSE</td>
                                            <Tooltip target="#ng-response-header"
                                                     content="To decimal point rounding is used in calculations, meaning numbers are adjusted to have only two digits after the decimal point (example: 3.456, 2nd decimal ='5'  and 3rd decimal='6' ( >=5-> round up); rounded: 3.46)"
                                            />
                                            <Tooltip target="#ng-request-header"
                                                     content="Please note that the CHO Requested Payment total also includes additional items like Delivery & Collection, CDW, Additional Driver charges etc..."
                                            />
                                        </tr>
                                        </thead>

                                        <tbody>
                                        <tr>
                                            <td>Total Hire Period</td>
                                            <td>{requestedOutcomeDays} days</td>
                                            <td>{outcomeDays} days</td>
                                            <td>{requestedOutcomeDays - outcomeDays} days</td>
                                            <td>{gtaResponseOutcomeDays} days</td>
                                        </tr>

                                        <tr>
                                            <td>Total Daily Rate</td>
                                            <td>£ {formatBigNo(requestedOutcomeAmount)}</td>
                                            <td>£ {formatBigNo(outcomeAmount)}</td>
                                            <td>£ {formatBigNo(requestedOutcomeAmount - outcomeAmount)}</td>
                                            <td>£ {formatBigNo(gtaResponseOutcomeAmount)}</td>
                                        </tr>

                                        <tr>
                                            <td>Total</td>
                                            <td>£ {formatBigNo(requestedTotal)}</td>
                                            <td>£ {formatBigNo(outcomeTotal)}</td>
                                            <td>£ {formatBigNo((requestedTotal) - (outcomeTotal))}</td>
                                            <td>£ {formatBigNo(gtaResponseOutcomeAmount * gtaResponseOutcomeDays)}</td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    {!disabled && <div className="text-center">
                                        <Button label="Edit amounts" className="mt-3"
                                                onClick={() => setShowEditAmountsModal(true)} icon="pi pi-pencil"/>
                                    </div>}

                                    {/* <div className="grid">
                            <div className="col-7">
                                <div className="mx-5">
                                    <InfoMessageTemplate message={outcomeMsg} />
                                </div>
                            </div>

                            <div className="col-5">
                                <div className="mx-5">
                                    <div className="flex justify-content-between mt-3 mb-2">
                                        <span className="ml-3">{outcomeDays} days</span>
                                        <span className="font-semibold">Total Hire Period Offered</span>
                                    </div>

                                    <div className="flex justify-content-between mb-3">
                                        <span>£ {formatBigNo(outcomeAmount)}</span>
                                        <span className="font-semibold">Hire Daily Rate Offered</span>
                                    </div>

                                    <hr />
                                    <div className="flex justify-content-between">
                                        <span>
                                            £ {formatBigNo(outcomeDays * outcomeAmount)} <span className="font-semibold">TOTAL</span>
                                        </span>

                                        {!disabled && (<Button label="Edit amounts" className="ml-3" onClick={() => setShowEditAmountsModal(true)} icon="pi pi-pencil" />)}
                                    </div>
                                </div>
                            </div>
                        </div> */}
                                </>
                                : <>
                                    <div>No outcomes available. Please run the rulesets in order to generate outcomes.
                                    </div>
                                    <hr/>
                                </>
                            }

                            <Button
                                className="p-button-outlined p-button-sm"
                                icon="pi pi-download"
                                disabled={disabledDownload}
                                onClick={downloadReport}
                            >Download
                            </Button>
                            <Accordion activeIndex={activeAccordionIndex}
                                       onTabChange={(e) => setActiveAccordionIndex(e.index)} multiple className="my-5">
                                {outcomes.map((outcome) => {
                                    return (
                                        <AccordionTab headerTemplate={() => headerTemplate(outcome)}
                                                      key={outcome.source + '_' + outcome.id}>
                                            {generateAccordionContent(outcome)}
                                        </AccordionTab>
                                    );
                                })}
                                {outcomesLoading && <ContentLoadingTemplate/>}
                            </Accordion>

                            <OutcomeAmountsModal visible={showEditAmountsModal} setVisible={setShowEditAmountsModal}
                                                 loadOutcomes={loadOutcomes}/>
                        </div>
                }
            </div>
        </OutcomesWrap>
    );
};

export default Outcomes;
